import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import {
  fetchJobList,
  fetchJobShiftsList,
  fetchJobStatsList,
  countActiveJobs,
  countNewApplicants,
  fetchJobShiftsCounters,
} from '@company/services/job/api';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';
import QueryKeys from '@constants/queryKeys';
import { IJobsData } from '@th-types/job.detail.type';
import {
  IJobListFilters,
  IJobShiftsCounters,
  IJobShiftsResult,
  IJobStatsList,
  JobListViews,
  NewApplicantsCounterResults,
} from '@th-types/jobs.type';
import { isMobile } from 'react-device-detect';

export const JOB_SHIFTS_PER_PAGE = 100;

export function useJobListQueries({
  jobListFilters,
  currentOffset,
}: {
  jobListFilters: IJobListFilters;
  currentOffset: number;
  jobList?: IJobsData;
}) {
  const queryJobList = [QueryKeys.COMPANY_JOBS];
  const queryJobShiftsList = [QueryKeys.COMPANY_JOB_SHIFTS_LIST];
  const queryJobShiftsCounters = [QueryKeys.COMPANY_JOB_SHIFTS_COUNTERS];
  const queryCountNewApplicants = [QueryKeys.COUNT_NEW_APPLICANTS];
  const queryCountActiveJobs = [QueryKeys.COUNT_ACTIVE_JOBS];

  const {
    isFetching: isFetchingJobs,
    error,
    data: jobListResult,
    refetch: refetchJobList,
  } = useQuery<IJobsData, AxiosError>({
    queryKey: queryJobList,
    queryFn: () =>
      fetchJobList(jobListFilters, currentOffset, DEFAULT_ITEMS_PER_PAGE),
    enabled: jobListFilters.viewType === JobListViews.JOBS,
  });

  const {
    data: jobShiftsListResult,
    isFetching: isFetchingJobShifts,
    refetch: refetchJobShifts,
  } = useQuery<IJobShiftsResult, AxiosError>({
    queryKey: queryJobShiftsList,
    queryFn: () =>
      fetchJobShiftsList(jobListFilters, currentOffset, JOB_SHIFTS_PER_PAGE),
    enabled: jobListFilters.viewType === JobListViews.SHIFTS,
  });

  const { data: jobShiftsCountersResult, refetch: refetchJobShiftsCounters } =
    useQuery<IJobShiftsCounters, AxiosError>({
      queryKey: queryJobShiftsCounters,
      queryFn: () =>
        fetchJobShiftsCounters(
          jobListFilters,
          currentOffset,
          DEFAULT_ITEMS_PER_PAGE
        ),
      enabled: jobListFilters.viewType === JobListViews.SHIFTS && !isMobile,
    });

  const queryJobStatsList = [
    QueryKeys.JOB_STATS_LIST,
    jobListResult?.results.map((item) => item.id) || [],
  ];

  const {
    data: jobStatsListResult,
    isFetching: isFetchingJobStatsList,
    refetch: refetchJobStatsList,
  } = useQuery<IJobStatsList, AxiosError>({
    queryKey: queryJobStatsList,
    queryFn: () =>
      fetchJobStatsList(jobListResult?.results.map((item) => item.id) ?? []),
    enabled:
      jobListFilters.viewType === JobListViews.JOBS &&
      jobListResult?.results !== undefined &&
      jobListResult.results.length > 0,
  });

  const queryJobShiftsStatsList = useMemo(() => {
    return [
      QueryKeys.JOB_SHIFTS_STATS_LIST,
      jobShiftsListResult?.jobs.map((item) => item.id),
    ];
  }, [jobShiftsListResult?.jobs]);

  const { data: jobShiftsStatsListResult, refetch: refetchJobShiftsStatsList } =
    useQuery<IJobStatsList, AxiosError>({
      queryKey: queryJobShiftsStatsList,
      queryFn: () =>
        fetchJobStatsList(jobShiftsListResult!.jobs.map((item) => item.id)!),
      enabled:
        jobListFilters.viewType === JobListViews.SHIFTS &&
        jobShiftsListResult?.jobs &&
        jobShiftsListResult.jobs.length > 0,
    });

  const { data: countActiveJobsResult, refetch: refetchCountActiveJobs } =
    useQuery<any, AxiosError>({
      queryKey: queryCountActiveJobs,
      queryFn: () => countActiveJobs(jobListFilters),
      enabled: jobListFilters.viewType === JobListViews.JOBS,
    });

  const {
    data: newApplicantsCountersResult,
    refetch: refetchCountNewApplicants,
  } = useQuery<NewApplicantsCounterResults, AxiosError>({
    queryKey: queryCountNewApplicants,
    queryFn: () => countNewApplicants(jobListFilters),
    enabled: jobListFilters.viewType === JobListViews.JOBS,
  });

  return {
    isFetchingJobs,
    isFetchingJobShifts,
    isFetchingJobStatsList,
    error,
    jobListResult,
    jobStatsListResult,
    jobShiftsListResult,
    countActiveJobsResult,
    jobShiftsCountersResult,
    jobShiftsStatsListResult,
    newApplicantsCountersResult,
    queryJobList,
    queryJobStatsList,
    queryJobShiftsList,
    queryCountActiveJobs,
    queryJobShiftsCounters,
    queryCountNewApplicants,
    queryJobShiftsStatsList,
    refetchJobList,
    refetchJobStatsList,
    refetchJobShifts,
    refetchJobShiftsCounters,
    refetchJobShiftsStatsList,
    refetchCountActiveJobs,
    refetchCountNewApplicants,
  };
}
